import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useChatUI } from '../context/ChatUIContext';
import { useT } from '../i18n';
import useSeo from '../hooks/useSeo';
import NavBrand from '../components/NavBrand';
import UserMenu from '../components/UserMenu';
import PublicNav from '../components/PublicNav';
import BackBar from '../components/BackBar';
import {
  PLANS, PLAN_IDS, SHOP_PLANS, SHOP_PLAN_IDS, CREDIT_PACKS, SIGNUP_CREDITS,
  planOf, planOfShop, packPrice, COSTS, isTestAccount,
} from '../constants/plans';
import { USER_TYPES } from '../constants/designer';
import useCredits from '../hooks/useCredits';
import TestAccountPanel from '../components/TestAccountPanel';
import './Dashboard.css';
import './Plans.css';

// ── PRICING ──────────────────────────────────────────────────────────────────
// Two things are being sold and they are not the same purchase, so they are two
// sections rather than one grid: a PLAN is a monthly relationship a designer
// enters into, a PACK is a one-off top-up anyone can buy. Mixing them into a
// single row of cards is how people end up subscribing when they wanted 30 more
// renders.
//
// EVERY NUMBER COMES FROM constants/plans.js. Nothing here is typed twice —
// prices, limits, credit counts and the per-credit maths are all read, so the
// page cannot drift from what the app actually enforces.
//
// WHAT THIS PAGE DOES NOT DO YET: take money. The buttons call `onChoose`,
// which is deliberately unwired — payment needs a provider, a webhook and a
// server-side grant, and a button that looks like it charges but does not is
// worse than one that says it is coming.

// ── THREE MARKS, NOT TWO ─────────────────────────────────────────────────────
// `yes` and `no` could not describe the entry tier honestly. Bronze does get a
// search boost, does place in recommendations and does wear its own profile
// design — just the smallest version of each — so a tick overstated it and a
// cross denied it outright. `mid` is that middle: a dash, drawn in the tier's
// own bronze so the mark and the card edge say the same thing.
//
// A LEVEL, not two booleans. Callers pass one of three strings, so no row can
// end up in an impossible state like "not included, but partial".
const Check = ({ level = 'yes' }) => (
  <span className={`pl-mark pl-mark--${level}`} aria-hidden="true">
    {level === 'yes' && (
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
        strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
    )}
    {level === 'mid' && (
      // A dash. Same weight and cap as the tick and the cross either side of
      // it, so the three marks read as one set — only the colour and the shape
      // change, never the drawing style.
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
        strokeWidth="2.6" strokeLinecap="round"><path d="M6 12h12" /></svg>
    )}
    {level === 'no' && (
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
        strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
    )}
  </span>
);

/**
 * Which mark a row shows for a plan.
 *
 * `mark` wins when a row needs all three levels; `bool` and `on` stay as the
 * two-state shorthand most rows use, and a row with none of them is ticked —
 * that is the case where having any value at all IS the feature.
 */
const markOf = (r, p) => {
  if (r.mark) return r.mark(p);
  if (r.bool) return r.bool(p) ? 'yes' : 'no';
  if (r.on)   return r.on(p) ? 'yes' : 'no';
  return 'yes';
};

// free buys no placement, bronze buys the entry-level version, silver and gold
// buy it outright. Keyed on searchRank because that is the number the search
// ordering, the recommendations rail and tierOf() all actually read.
const byRank = (p) => (p.searchRank === 0 ? 'no' : p.searchRank === 1 ? 'mid' : 'yes');

// A limit of Infinity reads as "Unlimited", never as "Infinity".
const limit = (n, word) => (n === Infinity ? word : n);

export default function Plans() {
  const { t } = useT();
  const { user, profile } = useAuth();
  const { credits, known, plan } = useCredits();
  const { openAdminChat } = useChatUI();
  // ── THE PLAN HAS TO BE THE LIVE ONE, NOT THE CACHED ONE ────────────────────
  // Read from useCredits' snapshot listener, NOT from AuthContext's `profile`.
  // `profile` is fetched once when the session starts — right for a name and an
  // avatar, wrong for the field this whole page is priced off. Every pack price
  // below runs through packPrice(pk, current), so a stale `current` quietly
  // quotes the wrong tier's discount.
  //
  // It showed up on the test account, which is the only thing that can change
  // its own plan from the UI: pressing Gold in the panel below moved the balance
  // and the panel's own highlight — both live — while the prices above sat at
  // free, with nothing on screen to say which one was lying.
  //
  // `planOf` takes the raw id as happily as a document, so there is no object to
  // reassemble here.
  const current = profile?.userType === USER_TYPES.SHOP ? planOfShop(plan) : planOf(plan);

  // ── WHO IS THIS PAGE FOR ───────────────────────────────────────────────────
  // Customers and designers are monetised differently, so they are shown
  // different things. A homeowner buys CREDITS — a one-off top-up — and has no
  // use for search ranking or portfolio limits; showing them a subscription
  // ladder invites them to buy the wrong product entirely.
  //
  // The role comes from `userType` on users/{uid}, which is the field the rest
  // of the app already branches on (USER_TYPES in constants/designer.js). No
  // second role system, and nothing read from the client's own claim about
  // itself — this is the same document the server writes.
  //
  const isDesigner = profile?.userType === USER_TYPES.DESIGNER;

  // A SHOP HAS ITS OWN LADDER, not the designer one relabelled — different
  // benefits, different prices, and no bronze rung at all. It used to fall
  // through to the customer view and see nothing but credit packs, which was
  // right only while there was nothing to sell it.
  const isShop = profile?.userType === USER_TYPES.SHOP;

  // Which table this account is measured against. Everything below reads
  // `tiers` / `tierIds` rather than naming one of the two directly, so one set
  // of markup renders both ladders and neither can be shown to the wrong role.
  const tiers = isShop ? SHOP_PLANS : PLANS;
  const tierIds = isShop ? SHOP_PLAN_IDS : PLAN_IDS;
  const hasLadder = isDesigner || isShop;

  // ── A SHOP DOES NOT BUY CREDITS ────────────────────────────────────────────
  // Credits pay for AI generation and a shop does not generate anything — it
  // lists products. Showing it a balance and five credit packs offers the one
  // thing on this page it can never use, and the packs are the largest block on
  // the screen.
  const sellsCredits = !isShop;
  // Signed out: a general overview. No balance, and no plan claimed on their
  // behalf — `current` stays Free only as a default for rendering, and the
  // "your plan" marker below is suppressed.
  const signedIn = Boolean(user);

  // ── THE TEST ACCOUNT BUYS BY CLICKING ────────────────────────────────────
  // For one address the buttons on this page apply immediately instead of
  // doing nothing: a plan is set, a pack is added to the balance. Nobody else
  // gets a click handler at all, so there is no code path that could fake a
  // purchase for a real user — the button is inert for them, exactly as before.
  //
  // The write is refused by security rule for any other account (see
  // isTestAccount in firestore.rules), so this is a convenience, not the
  // permission. Forcing it on in devtools yields permission-denied.
  const isTest = isTestAccount(user);
  const [busy, setBusy] = useState('');

  const applyTest = async (patch, key) => {
    if (!isTest || busy) return;
    setBusy(key);
    try {
      await updateDoc(doc(db, 'users', user.uid), patch);
      // Nothing to update locally — useCredits is a live listener, so the
      // balance here and in the header both follow the write on their own.
    } catch (err) {
      // Almost always the rules refusing it, which usually means the rules
      // deploy has not happened yet.
      console.error('[test account] write refused:', err?.code || err?.message);
    } finally {
      setBusy('');
    }
  };

  useSeo({
    title: 'Plans & Credits — Tunify',
    description: 'Designer plans and AI credit packs for Tunify. Start free, upgrade for placement, portfolio space and monthly credits.',
    path: '/plans',
  });

  const designerRows = [
    { k: 'pl_f_credits',   get: p => (p.monthlyCredits
      ? t('pl_per_mo').replace('{n}', p.monthlyCredits)
      : t('pl_once').replace('{n}', SIGNUP_CREDITS)) },
    { k: 'pl_f_portfolio', get: p => limit(p.portfolioImages, t('pl_unlimited')) },
    { k: 'pl_f_projects',  get: p => limit(p.projects, t('pl_unlimited')) },
    { k: 'pl_f_discount',  get: p => (p.packDiscount ? `${Math.round(p.packDiscount * 100)}%` : '—') },
    { k: 'pl_f_messaging', bool: p => p.messaging },

    // ── `on` SAYS WHETHER THE PLAN HAS THE FEATURE AT ALL ────────────────────
    // Separate from `get`, which says what it amounts to. Rank 0 is not a low
    // placement, it is the absence of one — the free tier buys no position — so
    // the row reads "Search position ✗ Standard": no boost, and here is where
    // that leaves you. Same for the three below.
    { k: 'pl_f_search', get: p => t(`pl_rank_${p.searchRank}`), mark: byRank },

    // ── RECOMMENDATIONS: A LEVEL, NOT A TICK ─────────────────────────────────
    // Was `bool: p => p.recommended`, which could only say yes or no — and the
    // truth has four steps, because the rail is RANKED rather than filtered.
    //
    // KEYED ON `searchRank`, NOT ON `recommended`. That is deliberate and it
    // matters: `recommended` is a boolean that is false on bronze, so a copy
    // keyed on it would have to claim bronze has no chance at all, and
    // RecommendationsFeed does not work that way — it orders by searchRank, so a
    // bronze designer really does place above a free one. Deriving the sentence
    // from the number that actually decides placement is what stops the pricing
    // page promising something the rail does not do.
    // ── SHORT LABEL, SHORT VALUES ────────────────────────────────────────────
    // "Recommended designers" against "Always recommended" needed two lines in a
    // ~250px card, and the wrapped label ran into the value beside it. Both
    // halves were saying "recommend"; the label keeps the word and the values
    // became a plain four-step scale, so the row fits on one line in all three
    // languages. See pl_rec_* in the i18n file.
    { k: 'pl_f_recommended', get: p => t(`pl_rec_${p.searchRank}`), mark: byRank },

    // `topDesigner` and `homepageFeature` no longer have rows here — removed
    // from the pricing page by request. THE FIELDS STAY in constants/plans.js:
    // they are real eligibility flags that isEligible() is built to check, and
    // deleting them would remove the capability, not just the advertisement.

    // Posts on the Tunify Instagram and TikTok. `2` reads as "2 or more" — see
    // the note on socialPromos in constants/plans.js.
    // One post a month is the entry level of this feature, not the whole of it —
    // so silver reads as a dash and only gold, at 2+, gets the tick. Keyed on
    // the count rather than on the plan id, so changing socialPromos in
    // constants/plans.js moves the mark with it.
    {
      k: 'pl_f_social',
      get: p => t(`pl_social_${p.socialPromos}`),
      mark: p => (p.socialPromos === 0 ? 'no' : p.socialPromos === 1 ? 'mid' : 'yes'),
    },

    // Which treatment their public profile header wears — the badge, the ring
    // and the chips built in components/TierMarks.jsx. Keyed on the plan id
    // because that is exactly what the `.dph--bronze|silver|gold` classes are
    // keyed on, so this row cannot describe a design the profile does not use.
    // `searchRank > 0`, matching tierOf() in components/TierMarks.jsx — that is
    // the exact test deciding whether a profile header wears a tier treatment at
    // all, so the tick here cannot promise a design the header will not draw.
    { k: 'pl_f_design', get: p => t(`pl_design_${p.id}`), mark: byRank },

    // ── THE BANNER ABOVE THEIR PROFILE ───────────────────────────────────────
    // A COUNT, not a tick, because the tiers differ by how many rather than by
    // whether: silver gets one still, gold gets three as a carousel, and those
    // are different products rather than more of the same. The value says which
    // one they are buying.
    //
    // Keyed on `announcements` rather than on the plan id, so the number here
    // and the number of slots the editor draws come from the same field — a
    // change in constants/plans.js moves both. `mark` mirrors the mechanism
    // exactly: 0 is the absence of the feature, 1 is the feature, 3 is the
    // carousel.
    {
      k: 'pl_f_banner',
      get: p => (p.announcements
        ? t(p.announcements > 1 ? 'pl_banner_many' : 'pl_banner_one').replace('{n}', p.announcements)
        : '—'),
      mark: p => (p.announcements === 0 ? 'no' : p.announcements === 1 ? 'mid' : 'yes'),
    },

    { k: 'pl_f_support',   bool: p => p.prioritySupport },
  ];

  // ── WHAT A SHOP IS BUYING ──────────────────────────────────────────────────
  // Its own list, not the designer rows with different labels. A shop has no
  // portfolio, no projects and no AI credits tied to its plan, and it has one
  // thing designers do not: how many pictures a listing may carry.
  //
  // Every value is read from SHOP_PLANS, so this table and what the app
  // enforces cannot drift.
  const shopRows = [
    {
      k: 'pl_f_prodimg',
      get: p => limit(p.productImages, t('pl_unlimited')),
      // Free has a real allowance rather than none, so it is a dash, not a
      // cross — 30 images is a working shop, just a capped one.
      mark: p => (p.productImages === Infinity ? 'yes' : 'mid'),
    },
    { k: 'pl_f_search', get: p => t(`pl_rank_${p.searchRank}`), mark: byRank },
    {
      k: 'pl_f_recshops',
      get: p => t(`pl_recshop_${p.recommended}`),
      // THREE STATES, because the rail is ranked and partly random: silver
      // competes for a place, gold is guaranteed one. A tick could not say
      // which of those a shop is paying for.
      mark: p => (p.recommended === 'none' ? 'no' : p.recommended === 'chance' ? 'mid' : 'yes'),
    },
    { k: 'pl_f_design', get: p => t(`pl_design_${p.pageStyle}`), mark: byRank },
    { k: 'pl_f_landing', on: p => p.landingPage },
    {
      k: 'pl_f_social',
      get: p => (p.socialDays ? t('pl_social_days').replace('{n}', p.socialDays) : '—'),
      mark: p => (p.socialDays === 0 ? 'no' : p.socialDays <= 2 ? 'mid' : 'yes'),
    },
    {
      k: 'pl_f_banner',
      get: p => (p.announcements
        ? t(p.announcements > 1 ? 'pl_banner_many' : 'pl_banner_one').replace('{n}', p.announcements)
        : '—'),
      mark: p => (p.announcements === 0 ? 'no' : p.announcements === 1 ? 'mid' : 'yes'),
    },
  ];

  const rows = isShop ? shopRows : designerRows;

  return (
    <div className="dash-root">
      <header className="dash-nav">
        <NavBrand />
        <div className="dash-nav-right">{user ? <UserMenu /> : <PublicNav />}</div>
      </header>
      <BackBar width={1180} fallback={user ? '/dashboard' : '/'} />

      <main className="dash-main pl-main">
        <h1 className="pl-title">{t(hasLadder ? 'pl_title' : 'pl_title_credits')}</h1>
        <p className="pl-lead">{t(isShop ? 'pl_lead_shop' : hasLadder ? 'pl_lead' : 'pl_lead_credits')}</p>

        {/* Balance first for everyone who HAS one — it is the thing they came
            to check, and on the customer view it is the only account state
            there is. Hidden when signed out, and hidden for shops, which have
            no balance; their plan pill still shows so the row is not empty. */}
        {signedIn && (sellsCredits ? known : hasLadder) && (
          <p className="pl-balance">
            {sellsCredits && t('pl_balance').replace('{n}', credits)}
            {hasLadder && <span className="pl-balance-plan">{t('pl_on_plan').replace('{p}', t(`pl_name_${current.id}`))}</span>}
          </p>
        )}

        {/* ── THE LADDER — DESIGNERS AND SHOPS, EACH THEIR OWN ── */}
        {hasLadder && (
        <div className="pl-grid">
          {tierIds.map(id => {
            const p = tiers[id];
            const isCurrent = signedIn && current.id === p.id;
            return (
              <section
                key={p.id}
                // `pl-card--<id>` on every card, styled only where a tier asks
                // for it — the same convention .sp-card--gold and
                // .rec-card--gold already use elsewhere, so adding silver or
                // gold later is a CSS block and no JSX change.
                className={`pl-card pl-card--${p.id}${p.popular ? ' pl-card--pop' : ''}${isCurrent ? ' pl-card--current' : ''}`}
              >
                {p.popular && <span className="pl-badge">{t('pl_popular')}</span>}
                <h2 className="pl-name">{t(`pl_name_${p.id}`)}</h2>
                <p className="pl-price">
                  {p.priceUsd === 0 ? t('pl_free') : <>${p.priceUsd}<span className="pl-per">/{t('pl_month')}</span></>}
                </p>
                <p className="pl-credits">
                  {p.monthlyCredits
                    ? t('pl_credits_mo').replace('{n}', p.monthlyCredits)
                    : t('pl_credits_once').replace('{n}', 4)}
                </p>

                <ul className="pl-feats">
                  {rows.map(r => (
                    <li key={r.k} className="pl-feat">
                      {/* ── THE TICK IS PER PLAN, EVEN ON A VALUE ROW ──────
                          This was `r.bool ? <Check on={r.bool(p)} /> : <Check on />`
                          — every row that showed a VALUE got a green tick no
                          matter what the value said. So Free was ticked for
                          "Recommendations: Never" and for a promotion row
                          reading "—", which claims the opposite of the words
                          beside it.

                          `on` is optional: rows where having any value at all
                          IS the feature (credits, portfolio, projects) leave it
                          out and stay ticked. */}
                      <Check level={markOf(r, p)} />
                      <span className="pl-feat-k">{t(r.k)}</span>
                      {/* KEYED ON `get`, NOT ON THE ABSENCE OF `bool`. A row
                          can be mark-only — "Shown on the homepage" is a yes or
                          a no with no value to print beside it — and `!r.bool`
                          called r.get on those, which is not there: `r.get is
                          not a function`, and the whole page failed to render.
                          Asking for the thing being used is the check that
                          cannot go stale as row shapes are added. */}
                      {r.get && <span className="pl-feat-v">{r.get(p)}</span>}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className={`pl-cta${p.popular ? ' pl-cta--pop' : ''}`}
                  disabled={isCurrent || busy === p.id}
                  onClick={isTest ? () => applyTest({ plan: p.id }, p.id) : undefined}
                >
                  {busy === p.id ? '…' : isCurrent ? t('pl_current') : t('pl_choose')}
                </button>
              </section>
            );
          })}
        </div>
        )}

        {/* ── CREDIT PACKS — everyone except shops, see sellsCredits ── */}
        {sellsCredits && (
        <>
        <h2 className="pl-h2">{t('pl_packs_title')}</h2>
        <p className="pl-lead">
          {t('pl_packs_lead').replace('{render}', COSTS.render).replace('{staging}', COSTS.staging)}
        </p>

        <div className="pl-packs">
          {CREDIT_PACKS.map(pk => {
            const price = packPrice(pk, current);
            const saved = price < pk.priceUsd;
            return (
              <button
                type="button"
                key={pk.id}
                className={`pl-pack${pk.best ? ' pl-pack--best' : ''}`}
                disabled={busy === pk.id}
                onClick={isTest
                  // A pack ADDS to the balance, as buying one would. Clamped
                  // to the same ceiling the security rule enforces, so the
                  // write cannot be refused for going over it.
                  ? () => applyTest({ credits: Math.min(10000, (credits || 0) + pk.credits) }, pk.id)
                  : undefined}
              >
                {pk.best && <span className="pl-pack-tag">{t('pl_best')}</span>}
                <span className="pl-pack-n">{pk.credits}</span>
                <span className="pl-pack-label">{t('pl_credits')}</span>
                <span className="pl-pack-price">
                  ${price.toFixed(2)}
                  {saved && <s className="pl-pack-was">${pk.priceUsd.toFixed(2)}</s>}
                </span>
                <span className="pl-pack-unit">
                  {t('pl_per_credit').replace('{n}', (price / pk.credits).toFixed(3))}
                </span>
              </button>
            );
          })}
        </div>
        </>
        )}

        {/* ── ANYTHING NOT ON THIS PAGE ────────────────────────────────────
            A pricing page can only answer the questions someone thought to
            print. This is the way out of it — and it opens the SAME admin
            conversation the Messages panel does, so a reply arrives where the
            reader already looks for messages rather than in a second inbox.

            Signed out there is no thread to open, so it points at sign-in
            through the existing auth flow instead of starting a second one. */}
        <section className="pl-ask">
          <div className="pl-ask-text">
            <h2 className="pl-ask-title">{t('pl_ask_title')}</h2>
            <p className="pl-ask-body">{t('pl_ask_body')}</p>
          </div>
          {signedIn ? (
            <button type="button" className="pl-ask-btn" onClick={openAdminChat}>
              {t('pl_ask_btn')}
            </button>
          ) : (
            <Link to="/login" className="pl-ask-btn">{t('pl_ask_btn')}</Link>
          )}
        </section>

        <p className="pl-note">{t('pl_soon')}</p>

        {/* Renders for one address only, and is enforced for that address by
            security rule rather than by this check. See TestAccountPanel. */}
        <TestAccountPanel />
      </main>
    </div>
  );
}
